import { useState, useEffect } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  BellRinging, WhatsappLogo, TelegramLogo, PaperPlaneTilt,
  CheckCircle, XCircle, ArrowClockwise, Trash, Info, ShieldCheck,
  Lightning, Sparkle, ChatText
} from '@phosphor-icons/react';

export default function AdminAlertsPage() {
  const [settings, setSettings] = useState({
    telegram_enabled: false,
    telegram_chat_id: '',
    telegram_bot_id: '',
    whatsapp_enabled: false,
    whatsapp_phone: '',
    whatsapp_api_id: '',
    alert_on_telegram: true,
    alert_on_whatsapp: true,
    include_media_info: true,
  });
  const [bots, setBots] = useState([]);
  const [waApis, setWaApis] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingChannel, setTestingChannel] = useState(null);
  const [showGuide, setShowGuide] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sRes, bRes, wRes, lRes] = await Promise.all([
        API.get('/admin-alerts/settings'),
        API.get('/bots').catch(() => ({ data: { bots: [] } })),
        API.get('/whatsapp/apis').catch(() => ({ data: { apis: [] } })),
        API.get('/admin-alerts/logs?limit=40').catch(() => ({ data: { logs: [] } })),
      ]);
      if (sRes.data.settings) setSettings(sRes.data.settings);
      setBots(bRes.data.bots || []);
      setWaApis(wRes.data.apis || []);
      setLogs(lRes.data.logs || []);
    } catch {
      toast.error('Failed to load alert settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async (customSettings) => {
    setSaving(true);
    const toSave = customSettings || settings;
    try {
      const res = await API.put('/admin-alerts/settings', toSave);
      setSettings(res.data.settings);
      toast.success('Alert settings saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (channel) => {
    setTestingChannel(channel);
    try {
      // Auto save latest values before testing
      await API.put('/admin-alerts/settings', settings);
      const res = await API.post('/admin-alerts/test', { channel });
      
      const r = res.data.result;
      const tgOk = r.telegram_status === 'sent';
      const waOk = r.whatsapp_status === 'sent';
      
      if (channel === 'telegram') {
        if (tgOk) toast.success('Test Telegram notification delivered!');
        else toast.error(r.telegram_error || `Telegram test failed (${r.telegram_status})`);
      } else if (channel === 'whatsapp') {
        if (waOk) toast.success('Test WhatsApp notification delivered!');
        else toast.error(r.whatsapp_error || `WhatsApp test failed (${r.whatsapp_status})`);
      } else {
        if (tgOk || waOk) toast.success('Test notification sent!');
        else toast.error('Test notification failed on all channels');
      }

      // Refresh logs
      const lRes = await API.get('/admin-alerts/logs?limit=40');
      setLogs(lRes.data.logs || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test alert failed');
    } finally {
      setTestingChannel(null);
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm('Clear all alert delivery logs?')) return;
    try {
      await API.delete('/admin-alerts/logs');
      setLogs([]);
      toast.success('Logs cleared');
    } catch {
      toast.error('Failed to clear logs');
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-10" data-testid="admin-alerts-page">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border pb-5">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <BellRinging size={22} weight="fill" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Super Admin Instant Alerts</h1>
              <p className="text-sm text-muted-foreground">
                Get real-time alerts on your Telegram and WhatsApp the second a user sends a message or media.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            className="gap-1.5"
            data-testid="refresh-alerts-btn"
          >
            <ArrowClockwise size={15} className={loading ? 'animate-spin' : ''} />
            Refresh
          </Button>
          <Button
            onClick={() => handleSave()}
            disabled={saving || loading}
            className="gap-1.5 bg-primary text-primary-foreground"
            data-testid="save-alerts-btn"
          >
            <ShieldCheck size={16} weight="bold" />
            {saving ? 'Saving...' : 'Save All Settings'}
          </Button>
        </div>
      </div>

      {/* Active Channels Overview Banner */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={`border ${settings.telegram_enabled ? 'border-sky-500/40 bg-sky-500/5' : 'border-border'}`}>
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#229ED9]/15 text-[#229ED9] flex items-center justify-center">
                <TelegramLogo size={24} weight="fill" />
              </div>
              <div>
                <div className="font-semibold text-sm">Telegram Alerts</div>
                <div className="text-xs text-muted-foreground">
                  {settings.telegram_enabled
                    ? settings.telegram_chat_id ? `ID: ${settings.telegram_chat_id}` : 'Enabled (No Chat ID)'
                    : 'Disabled'}
                </div>
              </div>
            </div>
            <Badge variant={settings.telegram_enabled && settings.telegram_chat_id ? 'default' : 'secondary'} className={settings.telegram_enabled && settings.telegram_chat_id ? 'bg-[#229ED9] text-white hover:bg-[#229ED9]/90' : ''}>
              {settings.telegram_enabled && settings.telegram_chat_id ? 'Active' : 'Off'}
            </Badge>
          </CardContent>
        </Card>

        <Card className={`border ${settings.whatsapp_enabled ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-border'}`}>
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center justify-center">
                <WhatsappLogo size={24} weight="fill" />
              </div>
              <div>
                <div className="font-semibold text-sm">WhatsApp Alerts</div>
                <div className="text-xs text-muted-foreground">
                  {settings.whatsapp_enabled
                    ? settings.whatsapp_phone ? `+${settings.whatsapp_phone}` : 'Enabled (No Phone)'
                    : 'Disabled'}
                </div>
              </div>
            </div>
            <Badge variant={settings.whatsapp_enabled && settings.whatsapp_phone ? 'default' : 'secondary'} className={settings.whatsapp_enabled && settings.whatsapp_phone ? 'bg-emerald-600 text-white hover:bg-emerald-600/90' : ''}>
              {settings.whatsapp_enabled && settings.whatsapp_phone ? 'Active' : 'Off'}
            </Badge>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/15 text-amber-500 flex items-center justify-center">
                <Lightning size={24} weight="fill" />
              </div>
              <div>
                <div className="font-semibold text-sm">Triggers</div>
                <div className="text-xs text-muted-foreground">
                  {[
                    settings.alert_on_telegram ? 'Telegram' : null,
                    settings.alert_on_whatsapp ? 'WhatsApp' : null
                  ].filter(Boolean).join(' + ') || 'None selected'}
                </div>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleTest('both')}
              disabled={testingChannel !== null}
              className="gap-1 text-xs h-7"
              data-testid="test-both-btn"
            >
              <PaperPlaneTilt size={13} weight="bold" />
              {testingChannel === 'both' ? 'Sending...' : 'Test Both'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Main Settings Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Telegram Alert Settings */}
        <Card className="border border-border shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-[#229ED9]/10 text-[#229ED9] flex items-center justify-center">
                  <TelegramLogo size={20} weight="fill" />
                </div>
                <div>
                  <CardTitle className="text-base font-bold">Telegram Alert Setup</CardTitle>
                  <CardDescription className="text-xs">Receive alerts directly in your personal Telegram chat</CardDescription>
                </div>
              </div>
              <Switch
                checked={settings.telegram_enabled}
                onCheckedChange={(val) => {
                  const updated = { ...settings, telegram_enabled: val };
                  setSettings(updated);
                  handleSave(updated);
                }}
                data-testid="telegram-alert-switch"
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="tg_chat_id" className="text-xs font-semibold">
                  Super Admin Telegram Chat ID <span className="text-red-500">*</span>
                </Label>
                <button
                  type="button"
                  onClick={() => setShowGuide(true)}
                  className="text-[11px] text-primary hover:underline flex items-center gap-1 font-medium"
                >
                  <Info size={13} />
                  How to find Chat ID?
                </button>
              </div>
              <Input
                id="tg_chat_id"
                placeholder="e.g. 123456789 (numeric ID)"
                value={settings.telegram_chat_id}
                onChange={(e) => setSettings({ ...settings, telegram_chat_id: e.target.value })}
                className="font-mono text-sm"
                data-testid="telegram-chat-id-input"
              />
              <p className="text-[11px] text-muted-foreground">
                Your personal numeric Telegram user/chat ID where notification messages will be delivered.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tg_bot_id" className="text-xs font-semibold">Send Alerts From Bot (Optional)</Label>
              <select
                id="tg_bot_id"
                value={settings.telegram_bot_id || ''}
                onChange={(e) => setSettings({ ...settings, telegram_bot_id: e.target.value })}
                className="w-full flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-testid="telegram-bot-select"
              >
                <option value="">⚡ Auto (First Active Bot)</option>
                {bots.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.bot_name} (@{b.bot_username || 'bot'}) {b.is_active ? '' : '(Inactive)'}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground">
                Make sure you have pressed <strong>/start</strong> on this bot in Telegram so it has permission to message you!
              </p>
            </div>

            <div className="pt-2 border-t border-border flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Verify Telegram delivery:</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTest('telegram')}
                disabled={testingChannel !== null}
                className="gap-1.5 text-xs text-[#229ED9] hover:bg-[#229ED9]/10"
                data-testid="test-telegram-btn"
              >
                <PaperPlaneTilt size={14} weight="bold" />
                {testingChannel === 'telegram' ? 'Testing...' : 'Send Test Telegram Alert'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp Alert Settings */}
        <Card className="border border-border shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
                  <WhatsappLogo size={20} weight="fill" />
                </div>
                <div>
                  <CardTitle className="text-base font-bold">WhatsApp Alert Setup</CardTitle>
                  <CardDescription className="text-xs">Receive alerts directly on your personal WhatsApp number</CardDescription>
                </div>
              </div>
              <Switch
                checked={settings.whatsapp_enabled}
                onCheckedChange={(val) => {
                  const updated = { ...settings, whatsapp_enabled: val };
                  setSettings(updated);
                  handleSave(updated);
                }}
                data-testid="whatsapp-alert-switch"
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="wa_phone" className="text-xs font-semibold">
                Super Admin WhatsApp Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="wa_phone"
                placeholder="e.g. +919876543210 or 919876543210 (with country code)"
                value={settings.whatsapp_phone}
                onChange={(e) => setSettings({ ...settings, whatsapp_phone: e.target.value })}
                className="font-mono text-sm"
                data-testid="whatsapp-phone-input"
              />
              <p className="text-[11px] text-muted-foreground">
                Your WhatsApp number including country code (e.g. 91 for India, 1 for USA).
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="wa_api_id" className="text-xs font-semibold">Send Alerts From WhatsApp API (Optional)</Label>
              <select
                id="wa_api_id"
                value={settings.whatsapp_api_id || ''}
                onChange={(e) => setSettings({ ...settings, whatsapp_api_id: e.target.value })}
                className="w-full flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-testid="whatsapp-api-select"
              >
                <option value="">⚡ Auto (First Active WABA / Meta API)</option>
                {waApis.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.display_name || a.phone_number_id} ({a.phone_number_id})
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground">
                {waApis.length === 0
                  ? 'No WhatsApp APIs configured yet. You can add one in the WhatsApp APIs tab.'
                  : 'Select which connected WhatsApp Business Account should send the notification.'}
              </p>
            </div>

            <div className="pt-2 border-t border-border flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Verify WhatsApp delivery:</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTest('whatsapp')}
                disabled={testingChannel !== null}
                className="gap-1.5 text-xs text-emerald-600 hover:bg-emerald-500/10"
                data-testid="test-whatsapp-btn"
              >
                <PaperPlaneTilt size={14} weight="bold" />
                {testingChannel === 'whatsapp' ? 'Testing...' : 'Send Test WhatsApp Alert'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert Triggers & Format Options */}
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <Sparkle size={18} className="text-primary" weight="fill" />
            Alert Trigger Conditions
          </CardTitle>
          <CardDescription className="text-xs">
            Choose which incoming channels should trigger instant notifications to the Super Admin
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
            <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-card/50">
              <div className="space-y-0.5 pr-2">
                <div className="text-xs font-semibold flex items-center gap-1.5">
                  <TelegramLogo size={14} weight="fill" className="text-[#229ED9]" />
                  Telegram Messages
                </div>
                <div className="text-[11px] text-muted-foreground">Alert when users message any Telegram bot</div>
              </div>
              <Switch
                checked={settings.alert_on_telegram}
                onCheckedChange={(val) => {
                  const updated = { ...settings, alert_on_telegram: val };
                  setSettings(updated);
                  handleSave(updated);
                }}
                data-testid="trigger-telegram-switch"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-card/50">
              <div className="space-y-0.5 pr-2">
                <div className="text-xs font-semibold flex items-center gap-1.5">
                  <WhatsappLogo size={14} weight="fill" className="text-emerald-600" />
                  WhatsApp Messages
                </div>
                <div className="text-[11px] text-muted-foreground">Alert when users message any WhatsApp number</div>
              </div>
              <Switch
                checked={settings.alert_on_whatsapp}
                onCheckedChange={(val) => {
                  const updated = { ...settings, alert_on_whatsapp: val };
                  setSettings(updated);
                  handleSave(updated);
                }}
                data-testid="trigger-whatsapp-switch"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-card/50">
              <div className="space-y-0.5 pr-2">
                <div className="text-xs font-semibold flex items-center gap-1.5">
                  <ChatText size={14} weight="fill" className="text-amber-500" />
                  Media Info Preview
                </div>
                <div className="text-[11px] text-muted-foreground">Include photo, video, or doc labels</div>
              </div>
              <Switch
                checked={settings.include_media_info}
                onCheckedChange={(val) => {
                  const updated = { ...settings, include_media_info: val };
                  setSettings(updated);
                  handleSave(updated);
                }}
                data-testid="trigger-media-switch"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alert Preview Banner */}
      <Card className="border border-border bg-muted/20">
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <ChatText size={16} className="text-muted-foreground" />
            Alert Message Preview
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 pb-4">
          <div className="p-3 bg-background rounded-md border border-border font-mono text-xs whitespace-pre-wrap leading-relaxed text-foreground/90 max-w-xl">
{`🔔 NEW INCOMING MESSAGE!

📱 Source: 🔵 Telegram (or 🟢 WhatsApp)
🤖 Channel/Bot: Support Bot (@MySupportBot)
👤 From: John Doe (@johndoe / +919876543210)
💬 Message: Hello, I have a question about my account!
⏰ Time: 11 Sep 2026, 04:00:00 PM IST`}
          </div>
        </CardContent>
      </Card>

      {/* Recent Alert Logs Table */}
      <Card className="border border-border">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base font-bold">Recent Alert Delivery History</CardTitle>
            <CardDescription className="text-xs">Live log of instant alerts sent to Super Admin</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const lRes = await API.get('/admin-alerts/logs?limit=40');
                setLogs(lRes.data.logs || []);
                toast.success('Logs refreshed');
              }}
              className="h-8 text-xs gap-1"
            >
              <ArrowClockwise size={13} />
              Refresh Logs
            </Button>
            {logs.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearLogs}
                className="h-8 text-xs gap-1 text-destructive hover:bg-destructive/10"
              >
                <Trash size={13} />
                Clear
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-muted/50 text-muted-foreground border-y border-border">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">Time (IST)</th>
                  <th className="px-4 py-2.5 font-semibold">Source</th>
                  <th className="px-4 py-2.5 font-semibold">Channel/Bot</th>
                  <th className="px-4 py-2.5 font-semibold">Sender</th>
                  <th className="px-4 py-2.5 font-semibold">Message Preview</th>
                  <th className="px-4 py-2.5 font-semibold">Telegram</th>
                  <th className="px-4 py-2.5 font-semibold">WhatsApp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground text-xs">
                      No alerts triggered yet. Enable Telegram or WhatsApp alerts above and test it out!
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-2.5 font-mono text-[11px] whitespace-nowrap text-muted-foreground">
                        {log.created_at ? log.created_at.slice(0, 19).replace('T', ' ') : '—'}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        {log.is_test ? (
                          <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/30">
                            TEST
                          </Badge>
                        ) : log.source_platform === 'telegram' ? (
                          <Badge variant="outline" className="text-[10px] bg-[#229ED9]/10 text-[#229ED9] border-[#229ED9]/30 gap-1">
                            <TelegramLogo size={10} weight="fill" /> Telegram
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/30 gap-1">
                            <WhatsappLogo size={10} weight="fill" /> WhatsApp
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-2.5 font-medium max-w-[150px] truncate">
                        {log.source_name || '—'}
                      </td>
                      <td className="px-4 py-2.5 max-w-[150px] truncate">
                        <div className="font-semibold">{log.sender_name || 'User'}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{log.sender_identifier}</div>
                      </td>
                      <td className="px-4 py-2.5 max-w-[200px] truncate text-muted-foreground">
                        {log.message_preview || '—'}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        {log.telegram_status === 'sent' ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                            <CheckCircle size={14} weight="fill" /> Sent
                          </span>
                        ) : log.telegram_status === 'failed' ? (
                          <span className="inline-flex items-center gap-1 text-red-500 font-medium" title={log.telegram_error}>
                            <XCircle size={14} weight="fill" /> Failed
                          </span>
                        ) : log.telegram_status === 'not_configured' ? (
                          <span className="text-amber-500">No Chat ID</span>
                        ) : (
                          <span className="text-muted-foreground">Skipped</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        {log.whatsapp_status === 'sent' ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                            <CheckCircle size={14} weight="fill" /> Sent
                          </span>
                        ) : log.whatsapp_status === 'failed' ? (
                          <span className="inline-flex items-center gap-1 text-red-500 font-medium" title={log.whatsapp_error}>
                            <XCircle size={14} weight="fill" /> Failed
                          </span>
                        ) : log.whatsapp_status === 'not_configured' ? (
                          <span className="text-amber-500">No Phone</span>
                        ) : (
                          <span className="text-muted-foreground">Skipped</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Guide Dialog: How to get Telegram Chat ID */}
      <Dialog open={showGuide} onOpenChange={setShowGuide}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TelegramLogo size={22} weight="fill" className="text-[#229ED9]" />
              How to get your Telegram Chat ID
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-xs text-muted-foreground leading-relaxed pt-2">
            <p>To receive instant alert messages in your personal Telegram account:</p>
            <ol className="list-decimal list-inside space-y-2 pl-1 text-foreground">
              <li>
                Open Telegram and search for <strong>@userinfobot</strong> or <strong>@raw_data_bot</strong>.
              </li>
              <li>
                Click <strong>Start</strong> or send any message to it.
              </li>
              <li>
                It will reply with your numerical <strong>Id</strong> (for example: <code className="bg-muted px-1 py-0.5 rounded font-mono text-[11px]">123456789</code>).
              </li>
              <li>
                Copy and paste that number into the <strong>Super Admin Telegram Chat ID</strong> field here.
              </li>
              <li>
                <strong>Important:</strong> Open the Telegram Bot you configured to send alerts, and press <strong>/start</strong> once so it is authorized to message you.
              </li>
            </ol>
          </div>
          <DialogFooter className="pt-2">
            <Button onClick={() => setShowGuide(false)} size="sm" className="w-full">
              Got it!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
